import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import "./SpecialSchools.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

/* ---------------- Inline icon set (no external icon package needed) ---------------- */

const IconSchool = ({ color = "#ffffff" }) => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 42V10l10-4 10 4v32" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    <path d="M14 42h20M10 42h28" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M20 18h8M20 24h8M20 30h8" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M22 42v-8h4v8" stroke={color} strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const IconHandsHeart = ({ color = "#ffffff" }) => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 26c-3-2.8-5.2-5.4-5.2-8.5a4.5 4.5 0 019-1 4.5 4.5 0 019 1c0 3.1-2.2 5.7-5.2 8.5l-3.8 3.6-3.8-3.6z"
      stroke={color}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M9 30c-2 2.2-3.5 4.8-3.5 8 0 4 3.3 7 7.3 7"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M39 30c2 2.2 3.5 4.8 3.5 8 0 4-3.3 7-7.3 7"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const IconPin = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 44s13-12.6 13-22a13 13 0 10-26 0c0 9.4 13 22 13 22z"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="24" cy="21" r="5" stroke="#ffffff" strokeWidth="2" />
  </svg>
);

const IconHandHeartSmall = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 30c-2.8-2.6-5-5-5-8a4 4 0 018-1 4 4 0 018 1c0 3-2.2 5.4-5 8l-3 3-3-3z"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M12 34c-2 0-6 1.5-6 6" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
    <path d="M36 34c2 0 6 1.5 6 6" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconBook = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 14c-3.5-2.5-8-3-13-2v22c5-1 9.5-.5 13 2 3.5-2.5 8-3 13-2V12c-5-1-9.5-.5-13 2z"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M24 14v22" stroke="#ffffff" strokeWidth="2" />
  </svg>
);

const IconGradCap = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12L4 20l20 8 20-8-20-8z" stroke="#ffffff" strokeWidth="2" strokeLinejoin="round" />
    <path d="M13 24v9c0 2.5 5 5 11 5s11-2.5 11-5v-9" stroke="#ffffff" strokeWidth="2" strokeLinejoin="round" />
    <path d="M40 20v10" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconHeart = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21s-7.5-4.6-10-9.3C.4 8.3 2 4.5 5.6 4c2.1-.3 4 .8 6.4 3 2.4-2.2 4.3-3.3 6.4-3 3.6.5 5.2 4.3 3.6 7.7C19.5 16.4 12 21 12 21z" />
  </svg>
);

const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M2.8 20c.7-3.6 3.3-5.6 6.2-5.6s5.5 2 6.2 5.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M15.5 5.4a3.2 3.2 0 010 6.2M18.8 20c-.5-2.6-1.9-4.4-3.8-5.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconHandHeart = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 38c-4-4-7-8-7-13a7 7 0 0114-1 7 7 0 0114 1c0 5-3 9-7 13l-7 7-7-7z"
      stroke="#ffffff"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path
      d="M14 34c-3 3-6 7-6 12 0 5 4 9 9 9h9c4 0 7-2 10-5l9-9c1.5-1.5 1.5-3.7 0-5-1.4-1.3-3.4-1.2-4.8.1l-6.2 6"
      fill="#ef7f3a"
    />
  </svg>
);

/* ------------------------------------------------------------------------------------ */

const SpecialSchools = () => {
  const centers = [
    {
      name: "Gop (Puri Dist.)",
      focus: "Focuses on imparting life skills to special children.",
      icon: <IconPin />,
    },
    {
      name: "Pottangi (Koraput)",
      focus: "A dedicated center for life skills development.",
      icon: <IconHandHeartSmall />,
    },
    {
      name: "Malkangiri",
      focus: "Provides essential life skills training for children with special needs.",
      icon: <IconBook />,
    },
    {
      name: "Semeliguda (Koraput)",
      focus: "A pre-school that prepares children for integration into mainstream primary schools.",
      icon: <IconGradCap />,
    },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="sc-hero">
        <div
          className="sc-hero__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/Special-school.png)",
          }}
        ></div>
        <div className="sc-hero__overlay"></div>
        <div className="sc-hero__dots"></div>

        <div className="sc-hero__logo">
          <IconHandHeart />
        </div>

        <svg className="sc-hero__wave" viewBox="0 0 200 260" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2 2c30 20-20 45 10 65s45-10 60 15-10 40 20 60"
            stroke="#ef7f3a"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>

        <Container className="sc-hero__content">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Special
            <br />
            <span>Schools</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Vikash supports special schools that provide tailored
            education and resources to meet the unique needs of children
            with disabilities.
          </motion.p>
        </Container>
      </section>

      {/* Introduction Section */}
      <section className="sc-intro">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center g-5">
              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="sc-icon-circle">
                  <IconSchool />
                </div>
                <div className="sc-eyebrow">Quality Education</div>
                <span className="sc-eyebrow__dash"></span>
                <h2 className="sc-heading">
                  Tailored Education
                  <br />
                  <span>for Every Child</span>
                </h2>
                <p className="sc-text">
                  Vikash has established dedicated centers for children
                  with special needs at various locations. These schools
                  focus on imparting crucial life skills and preparing
                  children for future integration into mainstream primary
                  schools. Our pre-school in Semeliguda, run with our own
                  resources, is a testament to our commitment to
                  providing a strong foundation for every child.
                </p>

                <div className="sc-badge">
                  <div className="sc-badge__icon">
                    <IconHandsHeart color="#ffffff" />
                  </div>
                  <div>
                    <strong>Every child deserves the right support.</strong>
                    <p>
                      We create learning spaces where every child can
                      grow with dignity and confidence.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="sc-intro__imgwrap">
                  <div className="sc-intro__dots"></div>
                  <div className="sc-intro__accent"></div>
                  <img
                    src="/assets/images/sections/SC.jpg"
                    alt="Child in a special school"
                    className="sc-intro__img"
                  />
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Our Centers Section */}
      <section className="sc-centers">
        <div className="sc-centers__dots sc-centers__dots--left"></div>
        <div className="sc-centers__dots sc-centers__dots--right"></div>

        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center sc-centers__head"
          >
            <div className="sc-centers__icon">
              <IconSchool color="#1a6b3c" />
            </div>
            <h2 className="sc-heading sc-heading--center">
              Our Centers <span>for Special Needs</span>
            </h2>
            <span className="sc-underline sc-underline--center"></span>
            <p className="sc-subtext">
              Each center is uniquely positioned to provide specialized
              care and education.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {centers.map((center, index) => (
                <Col md={6} lg={5} key={index}>
                  <motion.div variants={staggerItem} className="h-100">
                    <Card className="sc-card h-100">
                      <Card.Body className="d-flex align-items-start">
                        <div className="sc-card__icon">{center.icon}</div>
                        <div>
                          <Card.Title as="h5">{center.name}</Card.Title>
                          <span className="sc-card__underline"></span>
                          <Card.Text>{center.focus}</Card.Text>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Call To Action Section */}
      <section className="sc-cta">
        <Container>
          <motion.div
            className="sc-cta__panel"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <Row className="align-items-center g-0">
              <Col lg={6} className="sc-cta__left">
                <div className="sc-cta__icon">
                  <IconHandHeart />
                </div>
                <h2 className="sc-cta__title">
                  Support a Child's
                  <br />
                  <span>Education</span>
                </h2>
                <span className="sc-underline"></span>
                <p className="sc-cta__text">
                  Your contribution helps provide specialized resources
                  and a nurturing learning environment for children with
                  special needs.
                </p>
                <div className="sc-cta__actions">
                  <Button className="sc-btn sc-btn--solid" onClick={openDonation}>
                    <IconHeart /> Donate Now
                  </Button>
                  <Button
                    className="sc-btn sc-btn--outline"
                    onClick={() => navigate("/interns")}
                  >
                    <IconUsers /> Join Us
                  </Button>
                </div>
              </Col>

              <Col lg={6} className="sc-cta__right">
                <img
                  src="/assets/images/backgrounds/do3.png"
                  alt="Children in a special school classroom"
                  className="sc-cta__img"
                />
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default SpecialSchools;